import { Trans } from '@lingui/react/macro';
import BackButton from '@web/components/Shared/Button/BackButton';

import { useSummaryData } from '@web/components/Timesheet/hooks/useSummaryData';
import SummaryCards from '@web/components/Timesheet/SummaryCards';
import Box from 'carbon-react/lib/components/box';
import Icon from 'carbon-react/lib/components/icon';
import IconButton from 'carbon-react/lib/components/icon-button';
import Preview from 'carbon-react/lib/components/preview';
import Typography from 'carbon-react/lib/components/typography';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useIsStaff } from '../Timesheets/hooks';
import { ActionBarTitle } from './ActionBar/Title';
import { useActionBarTitle } from './ActionBar/useActionBarTitle';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]};
  height: 100%;
  padding: 0 ${({ theme }) => theme.space[3]};
  position: relative;
`;

const TopBarPreview = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0;
  width: 100%;
  height: 64px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  height: calc(100vh - 144px);
  gap: ${({ theme }) => theme.space[2]};
`;

const ClusterContentPreview = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.palette.slateTint(80)};
  flex: 0 0 379px;
  height: 100%;
  overflow: hidden;
  position: relative;
  padding: ${({ theme }) => theme.space[2]};

  & > div {
    height: 300px;
    width: 100%;
    display: flex;
  }
`;

const EditorContentPreview = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  transition: all 0.2s;
  height: 100%;
  gap: ${({ theme }) => theme.space[2]};
`;

const EditorContent = styled.div`
  display: flex;
  flex: 1;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.palette.slateTint(80)};
  padding: ${({ theme }) => theme.space[3]};
  position: relative;
  transition: all 0.2s;

  & > div {
    flex: 1;
  }
`;

const TSInfoRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.palette.slateTint(80)};
  padding: ${({ theme }) => theme.space[3]};
`;

const ChevronIcon = styled(Icon)<{ open: boolean }>`
  transition: transform 0.3s ease-in-out;
  transform: ${({ open }) => (open ? 'rotate(0deg)' : 'rotate(-90deg)')};
`;

const Container = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[4]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 400px;
  gap: ${({ theme }) => theme.space[1]};
`;
const FieldLabel = styled.div`
  color: ${({ theme }) => theme.palette.blackOpacity(0.9)};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;
const FieldContent = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  
  > * {
    flex: 1;
  }
`;

const PreviewTimesheetInformation = () => {
  return (
    <TSInfoRoot>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton aria-label="icon-button">
          <ChevronIcon fontSize="medium" open={true} type="chevron_down" />
        </IconButton>
        <Typography variant="segment-header">
          <Trans>Timesheet information</Trans>
        </Typography>
      </Box>
      <Container>
        <Field>
          <FieldLabel>
            <Preview height="17px" width="150px" />
          </FieldLabel>
          <FieldContent>
            <Preview height="40px" width="400px" />
          </FieldContent>
        </Field>
        <Field>
          <FieldLabel>
            <Preview height="17px" width="150px" />
          </FieldLabel>
          <FieldContent>
            <Preview height="40px" width="400px" />
          </FieldContent>
        </Field>
        <Field>
          <FieldLabel>
            <Preview height="17px" width="150px" />
          </FieldLabel>
          <FieldContent>
            <Preview height="40px" width="400px" />
          </FieldContent>
        </Field>
      </Container>
    </TSInfoRoot>
  );
};

const PreviewComponent = () => {
  const navigate = useNavigate();
  const staff = useIsStaff();
  const items = useSummaryData(undefined);

  const onBack = () => {
    navigate(staff ? '/staff-timesheets' : '/timesheets');
  };

  const actionBarTitle = useActionBarTitle();

  return (
    <Root>
      <TopBarPreview>
        <BackButton loading onBack={onBack} />
        <ActionBarTitle title={actionBarTitle} />
      </TopBarPreview>
      <SummaryCards disableRoundBorder items={items} />
      <PreviewTimesheetInformation />
      <Content>
        {!staff && (
          <ClusterContentPreview>
            <Preview height="300px" loading />
          </ClusterContentPreview>
        )}
        <EditorContentPreview>
          <EditorContent>
            <Preview height="100%" loading />
          </EditorContent>
        </EditorContentPreview>
      </Content>
    </Root>
  );
};

export default PreviewComponent;
